import {Container} from "@mui/material";
import React, {useEffect, useMemo, useState} from "react";
import {useAsync} from "react-async-hook";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {hasSimplifiedLayout, isOnlineBookingAvailable, OnlineBookingUnavailableNotice} from "../booking-utils";
import {AppLayout} from "../component/app-layout";
import {OrderContextProvider, OrderInitiation, OrderStep} from "../context/order";
import {useTenant} from "../context/tenant";
import {useSiteApi} from "../data/sites";
import {isBlank, notBlank} from "../utils";
import {OrderFlow} from "./order/order-flow";
import {OrderProgress} from "./order/order-progress";
import {OrderFlowSimplified} from "./order/simplifed/order-flow-simplified";

export function SiteRoutes() {
	const navigate = useNavigate();
	const {tenant} = useTenant();
	const {siteId, step} = useParams<{ siteId: string; step: OrderStep }>();
	const {state} = useLocation();
	const {getSite} = useSiteApi();

	// The /cubes/{cubeId}[/{slug}] resolver passes the scanned cube (and specialized-QR slug) in navigation
	// state. Capture it once on entry and hold it in the order context for the flow's lifetime. Navigation
	// state is scoped to this entry (no global key), so it can't leak into a later unrelated booking; it
	// survives step navigation via the order context. (A full page reload past the entry loses it —
	// acceptable: the fields are nullable.)
	const [initiation] = useState(() => (state as { initiation?: OrderInitiation } | null)?.initiation);

	const {result: site, error, loading} = useAsync(async () =>
		(notBlank(tenant?.id) && notBlank(siteId))
			? getSite(tenant?.id as string, siteId as string)
			: undefined, [tenant?.id, siteId]);

	const simplifiedLayout = useMemo(() => hasSimplifiedLayout(site), [site]);

	const onlineBookingAvailable = isOnlineBookingAvailable(site);

	useEffect(() => {
		if (isBlank(step) && onlineBookingAvailable && !simplifiedLayout) {
			navigate(`/${tenant?.id}/${siteId}/dates`, {replace: true});
		}
	}, [step, site, simplifiedLayout]);

	return (
		<AppLayout
			showBrandingBar={!simplifiedLayout}
			error={error}
			loading={loading}
		>
			{site !== undefined && !onlineBookingAvailable && (
				<Container maxWidth="md">
					<OnlineBookingUnavailableNotice />
				</Container>
			)}
			{site !== undefined && onlineBookingAvailable && (
				<OrderContextProvider initiation={initiation}>
					{!simplifiedLayout && (
						<>
							<OrderProgress site={site} />
							<OrderFlow site={site} />
						</>
					)}
					{simplifiedLayout && <OrderFlowSimplified site={site} />}
				</OrderContextProvider>
			)}
		</AppLayout>
	);
}
